import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import {styled} from '@mui/material/styles';
import AppTheme from '../mui-theme/AppTheme';
import ColorModeSelect from '../mui-theme/ColorModeSelect';
import {useNavigate} from "react-router-dom";
import {useCookies} from 'react-cookie'

const Card = styled(MuiCard)(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '450px',
    },
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const SignInContainer = styled(Stack)(({theme}) => ({
    height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    minHeight: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: -1,
        inset: 0,
        backgroundImage:
            'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
        backgroundRepeat: 'no-repeat',
        ...theme.applyStyles('dark', {
            backgroundImage:
                'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
        }),
    },
}));

export default function SignIn(props) {
    // State for the authentication flow
    const [authStep, setAuthStep] = React.useState('credentials'); // 'credentials' or '2fa'
    const [preAuthToken, setPreAuthToken] = React.useState('');

    // State for credentials form
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');

    // State for 2FA form
    const [twoFaCode, setTwoFaCode] = React.useState('');
    const [twoFaError, setTwoFaError] = React.useState('');

    // Navigation and cookies
    const navigate = useNavigate();
    const [cookies, setCookie] = useCookies(['session-token']);

    const handleCredentialSubmit = (event) => {
        event.preventDefault();
        if (!validateInputs()) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const email = data.get('email');
        const password = data.get('password');

        fetch('/api/sign-in', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({ email, password })
        }).then(async res => {
            const data = await res.json();
            if (res.ok) {
                return data;
            }
            // If response is not ok, throw an error with the message from the backend
            throw new Error(data.error || 'An unknown error occurred');
        }).then(data => {
            if (data.status === '2fa_required' && data.pre_auth_token) {
                setPreAuthToken(data.pre_auth_token);
                setAuthStep('2fa');
                // Clear any previous errors
                setPasswordError(false);
                setPasswordErrorMessage('');
                setEmailError(false);
                setEmailErrorMessage('');
            }
        }).catch(error => {
            // Handle specific errors from the backend
            if (error.message === 'unknown username') {
                setEmailError(true);
                setEmailErrorMessage('This email is not registered.');
            } else if (error.message === 'wrong password') {
                setPasswordError(true);
                setPasswordErrorMessage('The password is not correct.');
            } else {
                setPasswordError(true);
                setPasswordErrorMessage('An unexpected error occurred. Please try again.');
            }
            console.error(error);
        });
    };

    const handle2faSubmit = (event) => {
        event.preventDefault();
        setTwoFaError(''); // Reset error message

        if (twoFaCode.length !== 6 || !/^\d{6}$/.test(twoFaCode)) {
            setTwoFaError('Please enter a valid 6-digit code.');
            return;
        }

        fetch('/api/verify-2fa', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                pre_auth_token: preAuthToken,
                '2fa_code': twoFaCode,
            })
        }).then(res => res.json()).then(data => {
            if (data['token']) {
                setCookie('session-token', data['token'], { path: '/' });
                // The redirect will be handled by the App component upon cookie change.
            } else {
                setTwoFaError(data.error || 'An unknown error occurred.');
            }
        }).catch(err => {
            setTwoFaError('Verification failed. Please try again.');
            console.error(err);
        });
    };

    const validateInputs = () => {
        const email = document.getElementById('email');
        const password = document.getElementById('password');

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!password.value || password.value.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage('Password must be at least 6 characters long.');
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        return isValid;
    };

    return (
        <AppTheme {...props}>
            <CssBaseline enableColorScheme/>
            <SignInContainer direction="column" justifyContent="space-between">
                <ColorModeSelect sx={{position: 'fixed', top: '1rem', right: '1rem'}}/>
                <Card variant="outlined">
                    {authStep === 'credentials' && (
                        <>
                            <Typography
                                component="h1"
                                variant="h4"
                                sx={{width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)'}}
                            >
                                Sign in
                            </Typography>
                            <Box
                                component="form"
                                onSubmit={handleCredentialSubmit}
                                noValidate
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                    gap: 2,
                                }}
                            >
                                <FormControl>
                                    <FormLabel htmlFor="email">Email</FormLabel>
                                    <TextField
                                        error={emailError}
                                        helperText={emailErrorMessage}
                                        id="email"
                                        type="email"
                                        name="email"
                                        placeholder="your@email.com"
                                        autoComplete="off"
                                        autoFocus
                                        required
                                        fullWidth
                                        variant="outlined"
                                        color={emailError ? 'error' : 'primary'}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel htmlFor="password">Password</FormLabel>
                                    <TextField
                                        error={passwordError}
                                        helperText={passwordErrorMessage}
                                        name="password"
                                        placeholder="••••••"
                                        type="password"
                                        id="password"
                                        autoComplete="off"
                                        autoFocus
                                        required
                                        fullWidth
                                        variant="outlined"
                                        color={passwordError ? 'error' : 'primary'}
                                    />
                                </FormControl>
                                <FormControlLabel
                                    control={<Checkbox value="remember" color="primary"/>}
                                    label="Remember me"
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    onClick={validateInputs}
                                >
                                    Sign in
                                </Button>
                                <Link
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    sx={{alignSelf: 'center'}}
                                >
                                    Forgot your password?
                                </Link>
                            </Box>
                            <Divider>or</Divider>
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                <Typography sx={{textAlign: 'center'}}>
                                    Don&apos;t have an account?{' '}
                                    <Link
                                        href="/sign-in/"
                                        variant="body2"
                                        sx={{alignSelf: 'center'}}
                                    >
                                        Sign up
                                    </Link>
                                </Typography>
                            </Box>
                        </>
                    )}

                    {authStep === '2fa' && (
                        <>
                            <Typography
                                component="h1"
                                variant="h4"
                                sx={{width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)'}}
                            >
                                Two-Factor Authentication
                            </Typography>
                            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                                Please enter the 6-digit code from your authentication app.
                            </Typography>
                            <Box
                                component="form"
                                onSubmit={handle2faSubmit}
                                noValidate
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                    gap: 2,
                                }}
                            >
                                <FormControl>
                                    <FormLabel htmlFor="2fa-code">Verification Code</FormLabel>
                                    <TextField
                                        error={!!twoFaError}
                                        helperText={twoFaError}
                                        id="2fa-code"
                                        type="tel"
                                        name="2fa-code"
                                        placeholder="123456"
                                        autoComplete="one-time-code"
                                        autoFocus
                                        required
                                        fullWidth
                                        variant="outlined"
                                        value={twoFaCode}
                                        onChange={(e) => setTwoFaCode(e.target.value)}
                                        inputProps={{ maxLength: 6, pattern: "[0-9]*" }}
                                    />
                                </FormControl>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                >
                                    Verify Code
                                </Button>
                                 <Button
                                    type="button"
                                    fullWidth
                                    variant="text"
                                    onClick={() => setAuthStep('credentials')}
                                >
                                    Back to Sign In
                                </Button>
                            </Box>
                        </>
                    )}
                </Card>
            </SignInContainer>
        </AppTheme>
    );
}